import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Save, Loader2, Clock, Pencil, History } from 'lucide-react';
import { IconByName } from '../lib/tabIcons';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';

/**
 * Renders the admin-defined custom fields for a tab and lets authorised users
 * edit/save values per-patient. Read-only for patient role.
 *
 * Props:
 *   tab        — the tab object ({id, name, fields: [...]})
 *   patientId  — ObjectId of the patient
 *   canEdit    — boolean, whether the current user may edit values
 *   extraHeader — optional ReactNode rendered above the fields (used for builtin_override)
 */
export default function PatientCustomTabContent({ tab, patientId, canEdit, editing, extraHeader }) {
  const [values, setValues] = useState({});
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // When the parent page is in Edit mode, fields become editable (inline).
  // When not editing, render flat label/value pairs like the built-in tabs.
  const inEditMode = !!editing && !!canEdit;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/patients/${patientId}/custom-data/${tab.id}`);
      setValues(res.data.values || {});
      setMeta({ updated_at: res.data.updated_at, updated_by: res.data.updated_by });
      setDirty(false);
    } catch {
      setValues({});
    } finally {
      setLoading(false);
    }
  }, [patientId, tab.id]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (fieldId, v) => {
    setValues(prev => ({ ...prev, [fieldId]: v }));
    setDirty(true);
  };

  const handleSave = async () => {
    // Validate required
    for (const f of tab.fields || []) {
      if (f.type === 'section') continue;
      if (f.required) {
        const v = values[f.id];
        if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
          toast.error(`"${f.label}" is required`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const res = await api.put(`/patients/${patientId}/custom-data/${tab.id}`, { values });
      setMeta({ updated_at: res.data.updated_at, updated_by: res.data.updated_by });
      setDirty(false);
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Quick-edit single-field save — called inline from FlatFieldDisplay on blur/Enter.
  const quickSave = async (fieldId, newValue) => {
    const next = { ...values, [fieldId]: newValue };
    setValues(next);
    // Validate required for this field
    const f = (tab.fields || []).find(x => x.id === fieldId);
    if (f?.required && (newValue === undefined || newValue === null || newValue === '' || (Array.isArray(newValue) && newValue.length === 0))) {
      toast.error(`"${f.label}" is required`);
      return false;
    }
    try {
      const res = await api.put(`/patients/${patientId}/custom-data/${tab.id}`, { values: next });
      setMeta({ updated_at: res.data.updated_at, updated_by: res.data.updated_by });
      setDirty(false);
      toast.success('Saved');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
      return false;
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0E7490]" /></div>;
  }

  const fields = tab.fields || [];

  return (
    <div className="space-y-4" data-testid={`custom-tab-content-${tab.id}`}>
      {extraHeader}

      {fields.length === 0 ? (
        <div className="text-center py-6 text-sm text-[#94A3B8]">
          This tab has no fields yet. An admin can add fields in <span className="font-medium">Settings → Patient Tabs</span>.
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {groupFieldsBySection(fields).map((group, gi) => (
              <div key={gi}>
                {group.title && (
                  <div className="flex items-center gap-2 mb-2 mt-2 first:mt-0 border-b border-[#E2E8F0] pb-1.5" data-testid={`custom-section-${gi}`}>
                    <h3 className="text-sm font-semibold text-[#0A2342] uppercase tracking-wider">{group.title}</h3>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {group.fields.map(f => (
                    inEditMode ? (
                      <FieldRenderer
                        key={f.id}
                        field={f}
                        value={values[f.id]}
                        onChange={(v) => handleChange(f.id, v)}
                        disabled={false}
                      />
                    ) : (
                      <FlatFieldDisplay
                        key={f.id}
                        field={f}
                        value={values[f.id]}
                        canEdit={canEdit}
                        onQuickSave={(newVal) => quickSave(f.id, newVal)}
                        patientId={patientId}
                        tabId={tab.id}
                      />
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>

          {inEditMode && (
            <div className="flex items-center justify-between pt-3 border-t border-[#E2E8F0] flex-wrap gap-2">
              <div className="text-[11px] text-[#64748B] flex items-center gap-1">
                {meta.updated_at ? (
                  <>
                    <Clock className="w-3 h-3" />
                    Last saved {new Date(meta.updated_at).toLocaleString()}
                    {meta.updated_by && ` by ${meta.updated_by}`}
                  </>
                ) : (
                  <span>Not saved yet</span>
                )}
              </div>
              <Button
                className="careiq-btn-primary flex items-center gap-2"
                onClick={handleSave}
                disabled={saving || !dirty}
                data-testid={`save-custom-tab-${tab.id}`}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {dirty ? 'Save Changes' : 'Saved'}
              </Button>
            </div>
          )}
          {!inEditMode && canEdit && meta.updated_at && (
            <div className="text-[11px] text-[#64748B] flex items-center gap-1 pt-2 border-t border-[#E2E8F0]" data-testid={`custom-tab-meta-${tab.id}`}>
              <Clock className="w-3 h-3" />
              Last saved {new Date(meta.updated_at).toLocaleString()}{meta.updated_by && ` by ${meta.updated_by}`}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function groupFieldsBySection(fields) {
  // Splits a flat field list into groups keyed by preceding `section` field.
  const groups = [];
  let current = { title: null, fields: [] };
  for (const f of fields || []) {
    if (f.type === 'section') {
      if (current.fields.length > 0 || current.title) groups.push(current);
      current = { title: f.label || 'Section', fields: [] };
    } else {
      current.fields.push(f);
    }
  }
  if (current.fields.length > 0 || current.title) groups.push(current);
  return groups;
}

function FieldRenderer({ field, value, onChange, disabled }) {
  const label = (
    <Label className="careiq-label flex items-center gap-1.5">
      {field.icon && <IconByName name={field.icon} className="w-3.5 h-3.5 text-[#64748B]" />}
      {field.label}
      {field.required && <span className="text-[#991B1B]">*</span>}
    </Label>
  );
  const placeholder = field.placeholder || '';
  const common = {
    className: 'careiq-input',
    disabled,
    placeholder,
    'data-testid': `field-input-${field.id}`,
  };

  switch (field.type) {
    case 'textarea':
      return (
        <div className="md:col-span-2">
          {label}
          <textarea
            {...common}
            className="careiq-input min-h-[90px]"
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
          />
        </div>
      );
    case 'number':
      return (
        <div>{label}<Input type="number" {...common} value={value ?? ''} onChange={e => onChange(e.target.value)} /></div>
      );
    case 'date':
      return (
        <div>{label}<Input type="date" {...common} value={value ?? ''} onChange={e => onChange(e.target.value)} /></div>
      );
    case 'email':
      return (
        <div>{label}<Input type="email" {...common} value={value ?? ''} onChange={e => onChange(e.target.value)} /></div>
      );
    case 'phone':
      return (
        <div>{label}<Input type="tel" {...common} value={value ?? ''} onChange={e => onChange(e.target.value)} /></div>
      );
    case 'checkbox':
      return (
        <div className="flex items-center gap-2 pt-5">
          <Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} disabled={disabled} data-testid={`field-input-${field.id}`} />
          <span className="text-sm">{field.label}{field.required && <span className="text-[#991B1B]"> *</span>}</span>
        </div>
      );
    case 'select':
      return (
        <div>
          {label}
          <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger data-testid={`field-input-${field.id}`}><SelectValue placeholder={placeholder || 'Select…'} /></SelectTrigger>
            <SelectContent>
              {(field.options || []).map((o, i) => <SelectItem key={i} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    case 'file':
      return (
        <div className="md:col-span-2">
          {label}
          <div className="p-3 rounded border border-dashed border-[#CBD5E1] bg-[#FAFBFC] text-xs text-[#64748B]" data-testid={`field-input-${field.id}`}>
            Use the <strong>Files</strong> tab to upload documents and images for this patient. File-type fields in custom tabs are coming soon.
          </div>
        </div>
      );
    default: // text
      return (
        <div>{label}<Input type="text" {...common} value={value ?? ''} onChange={e => onChange(e.target.value)} /></div>
      );
  }
}


/**
 * Flat read-only view of a custom field with optional inline "quick-edit":
 * - Click anywhere on the row to enter inline edit mode (hover shows pencil).
 * - Enter / blur ⇒ save. Escape ⇒ cancel.
 * - Loading spinner while saving.
 * - Works for text, textarea, number, date, select, checkbox, email, phone.
 * - Read-only types (file) just render flat with no click handler.
 */
function FlatFieldDisplay({ field, value, canEdit, onQuickSave, patientId, tabId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const inputRef = React.useRef(null);

  const loadHistory = async () => {
    if (history !== null) return;
    setHistoryLoading(true);
    try {
      const r = await api.get(`/patients/${patientId}/custom-data/${tabId}/history?field=${encodeURIComponent(field.id)}&limit=20`);
      setHistory(r.data?.entries || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  React.useEffect(() => { setDraft(value); }, [value, editing]);
  React.useEffect(() => {
    if (editing && inputRef.current) {
      try {
        inputRef.current.focus();
        if (inputRef.current.select) inputRef.current.select();
      } catch (e) { /* noop */ }
    }
  }, [editing]);

  const quickEditable = canEdit && onQuickSave && field.type !== 'file';

  const doSave = async (newVal) => {
    if (newVal === value) { setEditing(false); return; }
    setSaving(true);
    const ok = await onQuickSave(newVal);
    setSaving(false);
    if (ok) setEditing(false);
  };

  const cancel = () => { setDraft(value); setEditing(false); };

  let displayNode;
  if (value === null || value === undefined || value === '') {
    displayNode = <span className="text-[#94A3B8] italic">{quickEditable ? 'Click to add' : '—'}</span>;
  } else if (Array.isArray(value)) {
    displayNode = value.length > 0 ? value.join(', ') : <span className="text-[#94A3B8]">—</span>;
  } else if (typeof value === 'boolean') {
    displayNode = value ? 'Yes' : 'No';
  } else {
    displayNode = String(value);
  }

  if (!editing) {
    return (
      <div
        className={`flex items-start gap-2 py-1 px-2 -mx-2 rounded ${quickEditable ? 'cursor-pointer hover:bg-[#F0FDFA] group' : ''}`}
        onClick={quickEditable ? () => setEditing(true) : undefined}
        data-testid={`flat-field-${field.id}`}
      >
        {field.icon && <IconByName name={field.icon} className="w-3.5 h-3.5 text-[#64748B] mt-0.5 shrink-0" />}
        <div className="min-w-0 flex-1">
          <span className="text-[#64748B] text-xs">{field.label}:</span>{' '}
          <span className="text-sm text-[#0A2342] font-medium break-words">{displayNode}</span>
        </div>
        {quickEditable && (
          <Pencil className="w-3 h-3 text-[#94A3B8] opacity-0 group-hover:opacity-100 mt-1 shrink-0" aria-hidden="true" />
        )}
        {patientId && tabId && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); loadHistory(); }}
                className="opacity-0 group-hover:opacity-100 text-[#94A3B8] hover:text-[#0E7490] mt-0.5 shrink-0"
                title="View change history"
                data-testid={`field-history-${field.id}`}
              >
                <History className="w-3 h-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="text-xs font-semibold text-[#0A2342] mb-2 flex items-center gap-1">
                <History className="w-3.5 h-3.5 text-[#0E7490]" /> Change history — {field.label}
              </div>
              {historyLoading ? (
                <div className="text-xs text-[#64748B] py-2"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading…</div>
              ) : !history || history.length === 0 ? (
                <div className="text-xs text-[#94A3B8] py-2">No edits recorded yet.</div>
              ) : (
                <ul className="space-y-2 max-h-64 overflow-y-auto text-xs" data-testid={`field-history-list-${field.id}`}>
                  {history.map(h => (
                    <li key={h.id} className="border-l-2 border-[#0E7490]/30 pl-2">
                      <div className="text-[#0A2342] font-medium">{h.changed_by || '—'}<span className="text-[10px] text-[#94A3B8]"> · {h.changed_by_role?.replace(/_/g, ' ')}</span></div>
                      <div className="text-[10px] text-[#94A3B8]">{new Date(h.changed_at).toLocaleString()}</div>
                      <div className="mt-1 text-[#475569] break-words">
                        <span className="line-through opacity-70">{formatVal(h.before)}</span>{' → '}<span className="font-medium text-[#0A2342]">{formatVal(h.after)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
    );
  }

  // EDITING
  const commitOnBlur = () => doSave(draft);

  const renderInput = () => {
    const common = {
      ref: inputRef,
      disabled: saving,
      onBlur: commitOnBlur,
      onKeyDown: (e) => {
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        else if (e.key === 'Enter' && field.type !== 'textarea') { e.preventDefault(); doSave(draft); }
      },
    };
    switch (field.type) {
      case 'textarea':
        return <textarea {...common} value={draft ?? ''} onChange={(e) => setDraft(e.target.value)} rows={3} className="careiq-input w-full" data-testid={`quickedit-input-${field.id}`} />;
      case 'number':
        return <Input {...common} type="number" value={draft ?? ''} onChange={(e) => setDraft(e.target.value === '' ? '' : Number(e.target.value))} className="careiq-input h-8" data-testid={`quickedit-input-${field.id}`} />;
      case 'date':
        return <Input {...common} type="date" value={draft ?? ''} onChange={(e) => setDraft(e.target.value)} className="careiq-input h-8" data-testid={`quickedit-input-${field.id}`} />;
      case 'email':
        return <Input {...common} type="email" value={draft ?? ''} onChange={(e) => setDraft(e.target.value)} className="careiq-input h-8" data-testid={`quickedit-input-${field.id}`} />;
      case 'phone':
        return <Input {...common} type="tel" value={draft ?? ''} onChange={(e) => setDraft(e.target.value)} className="careiq-input h-8" data-testid={`quickedit-input-${field.id}`} />;
      case 'select':
        return (
          <Select
            open
            value={draft ?? ''}
            onValueChange={async (v) => { setDraft(v); await doSave(v); }}
            onOpenChange={(o) => { if (!o) cancel(); }}
          >
            <SelectTrigger className="h-8" data-testid={`quickedit-input-${field.id}`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {(field.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case 'checkbox':
        return (
          <div className="flex items-center gap-2 py-1">
            <Checkbox
              checked={!!draft}
              onCheckedChange={async (c) => { setDraft(!!c); await doSave(!!c); }}
              data-testid={`quickedit-input-${field.id}`}
            />
            <span className="text-xs text-[#64748B]">Yes / No</span>
          </div>
        );
      default: // text
        return <Input {...common} value={draft ?? ''} onChange={(e) => setDraft(e.target.value)} className="careiq-input h-8" data-testid={`quickedit-input-${field.id}`} />;
    }
  };

  return (
    <div className="flex items-start gap-2 py-1 px-2 -mx-2 rounded bg-[#F0FDFA] border border-[#0E7490]/20" data-testid={`flat-field-edit-${field.id}`}>
      {field.icon && <IconByName name={field.icon} className="w-3.5 h-3.5 text-[#0E7490] mt-2 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] text-[#64748B] mb-0.5">{field.label}</div>
        {renderInput()}
      </div>
      {saving && <Loader2 className="w-3 h-3 animate-spin text-[#0E7490] mt-2 shrink-0" />}
    </div>
  );
}

function formatVal(v) {
  if (v === null || v === undefined || v === '') return <span className="text-[#94A3B8] italic">empty</span>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}
